import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { platformIntegrationsAPI } from '../lib/api';
import { getPlatformColor } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  Plug,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Trash2,
  RefreshCw,
  Save,
  Eye,
  EyeOff,
  Clock,
  Zap,
} from 'lucide-react';

export const PlatformIntegrations = () => {
  const { isSuperAdmin } = useAuth();
  const [platformConfig, setPlatformConfig] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [syncing, setSyncing] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  // Form state for each platform
  const [formData, setFormData] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, integrationsRes, syncStatusRes] = await Promise.all([
        platformIntegrationsAPI.getConfig(),
        platformIntegrationsAPI.getAll(),
        platformIntegrationsAPI.getSyncStatus().catch(() => ({ data: null })),
      ]);
      
      setPlatformConfig(configRes.data.platforms);
      setIntegrations(integrationsRes.data);
      setSyncStatus(syncStatusRes.data);
      
      // Initialize form data with saved credentials
      const initialFormData = {};
      for (const platform of Object.keys(configRes.data.platforms)) {
        const saved = integrationsRes.data.find(i => i.platform === platform);
        initialFormData[platform] = {
          credentials: saved?.credentials || {},
          is_active: saved?.is_active ?? true,
        };
      }
      setFormData(initialFormData);
    } catch (error) {
      toast.error('Failed to fetch platform configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh sync status every 30 seconds
    const interval = setInterval(async () => {
      try {
        const res = await platformIntegrationsAPI.getSyncStatus();
        setSyncStatus(res.data);
      } catch (error) {
        console.error('Failed to refresh sync status:', error);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const getIntegrationStatus = (platform) => {
    const integration = integrations.find(i => i.platform === platform);
    return integration?.connection_status || 'not_configured';
  };

  const getIntegrationInfo = (platform) => {
    return integrations.find(i => i.platform === platform);
  };

  const handleInputChange = (platform, field, value) => {
    setFormData(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        credentials: {
          ...prev[platform]?.credentials,
          [field]: value,
        },
      },
    }));
  };

  const handleActiveToggle = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        is_active: value,
      },
    }));
  };

  const handleSave = async (platform) => {
    setSaving(prev => ({ ...prev, [platform]: true }));
    try {
      await platformIntegrationsAPI.save({
        platform,
        credentials: formData[platform]?.credentials || {},
        is_active: formData[platform]?.is_active ?? true,
      });
      toast.success(`${platformConfig[platform]?.name || platform} credentials saved`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleTest = async (platform) => {
    setTesting(prev => ({ ...prev, [platform]: true }));
    try {
      const result = await platformIntegrationsAPI.test(platform);
      if (result.data.status === 'connected') {
        toast.success(`${platformConfig[platform]?.name || platform}: Connection successful!`);
      } else {
        toast.error(`${platformConfig[platform]?.name || platform}: Connection failed`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleSync = async (platform) => {
    setSyncing(prev => ({ ...prev, [platform]: true }));
    try {
      const result = await platformIntegrationsAPI.sync(platform);
      toast.success(result.data.message);
      if (result.data.note) {
        toast.info(result.data.note);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleDelete = async (platform) => {
    if (!window.confirm(`Are you sure you want to remove ${platformConfig[platform]?.name || platform} integration?`)) {
      return;
    }
    try {
      await platformIntegrationsAPI.delete(platform);
      toast.success('Integration removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove integration');
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      await platformIntegrationsAPI.syncAll();
      toast.success('Sync started for all connected platforms');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleClearDemoData = async () => {
    if (!window.confirm('Are you sure you want to clear all demo data? This will remove seeded campaigns and performance data. Real synced data will be preserved.')) {
      return;
    }
    setClearingData(true);
    try {
      const result = await platformIntegrationsAPI.clearDemoData();
      toast.success(`Cleared ${result.data.campaigns_deleted} campaigns and ${result.data.platform_data_deleted} performance records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear demo data');
    } finally {
      setClearingData(false);
    }
  };

  const togglePasswordVisibility = (platform, field) => {
    setShowPasswords(prev => ({
      ...prev,
      [`${platform}_${field}`]: !prev[`${platform}_${field}`],
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'not_tested':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      default:
        return <Plug className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'error':
        return 'Error';
      case 'not_tested':
        return 'Not Tested';
      default:
        return 'Not Configured';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'not_tested':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Platform Integrations</h1>
            <p className="text-sm text-slate-500 mt-1">
              Connect your advertising platforms to automatically sync campaign data
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSyncAll}
              disabled={syncingAll}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {syncingAll ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Sync All Platforms
            </Button>
          </div>
        </div>

        {/* Sync Status Card */}
        {syncStatus && (
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${syncStatus.is_running ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                  <div>
                    <p className="font-medium text-slate-900">
                      Auto-Sync: {syncStatus.is_running ? 'Running' : 'Stopped'}
                    </p>
                    <p className="text-xs text-slate-500">
                      Data is automatically fetched every 10 minutes
                    </p>
                  </div>
                </div>
                {syncStatus.next_run && (
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Next sync:</p>
                    <p className="text-sm font-mono text-slate-700">
                      {new Date(syncStatus.next_run).toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Cards */}
        <div className="space-y-4">
          <Accordion type="multiple" className="space-y-4">
            {Object.entries(platformConfig).map(([platform, config]) => {
              const status = getIntegrationStatus(platform);
              const integration = getIntegrationInfo(platform);
              const isConfigured = !!integration;

              return (
                <AccordionItem
                  key={platform}
                  value={platform}
                  className="border border-slate-200 rounded-lg overflow-hidden bg-white"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50">
                    <div className="flex items-center gap-4 w-full">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: getPlatformColor(platform) + '20' }}
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getPlatformColor(platform) }}
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-slate-900">{config.name}</h3>
                        <p className="text-sm text-slate-500">{platform}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${getStatusColor(status)}`}>
                          {getStatusIcon(status)}
                          {getStatusText(status)}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="pt-4 border-t border-slate-100">
                      {/* Documentation Link */}
                      {config.docs_url && (
                        <a
                          href={config.docs_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mb-4"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View API Documentation
                        </a>
                      )}

                      {/* Credential Fields */}
                      <div className="grid gap-4 mt-4">
                        {config.fields.map((field) => (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={`${platform}_${field.key}`} className="flex items-center gap-1">
                              {field.label}
                              {field.required && <span className="text-red-500">*</span>}
                            </Label>
                            <div className="relative">
                              <Input
                                id={`${platform}_${field.key}`}
                                type={
                                  field.type === 'password' && !showPasswords[`${platform}_${field.key}`]
                                    ? 'password'
                                    : field.type === 'email' ? 'email' : 'text'
                                }
                                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                                value={formData[platform]?.credentials?.[field.key] || ''}
                                onChange={(e) => handleInputChange(platform, field.key, e.target.value)}
                                className="pr-10"
                              />
                              {field.type === 'password' && (
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility(platform, field.key)}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                  {showPasswords[`${platform}_${field.key}`] ? (
                                    <EyeOff className="w-4 h-4" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                            {integration?.masked_credentials?.[field.key] && (
                              <p className="text-xs text-slate-500">
                                Current: {integration.masked_credentials[field.key]}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Active Toggle */}
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                        <div>
                          <Label htmlFor={`${platform}_active`}>Active</Label>
                          <p className="text-xs text-slate-500">Enable automatic data sync</p>
                        </div>
                        <Switch
                          id={`${platform}_active`}
                          checked={formData[platform]?.is_active ?? true}
                          onCheckedChange={(value) => handleActiveToggle(platform, value)}
                        />
                      </div>

                      {/* Last Sync Info */}
                      {integration?.last_sync && (
                        <p className="text-xs text-slate-500 mt-4">
                          Last synced: {new Date(integration.last_sync).toLocaleString()}
                        </p>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-3 mt-6">
                        <Button
                          onClick={() => handleSave(platform)}
                          disabled={saving[platform]}
                          className="bg-slate-900 hover:bg-slate-800"
                        >
                          {saving[platform] ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Credentials
                        </Button>

                        {isConfigured && (
                          <>
                            <Button
                              variant="outline"
                              onClick={() => handleTest(platform)}
                              disabled={testing[platform]}
                            >
                              {testing[platform] ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Plug className="w-4 h-4 mr-2" />
                              )}
                              Test Connection
                            </Button>

                            {status === 'connected' && (
                              <Button
                                variant="outline"
                                onClick={() => handleSync(platform)}
                                disabled={syncing[platform]}
                              >
                                {syncing[platform] ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                )}
                                Sync Data
                              </Button>
                            )}

                            <Button
                              variant="ghost"
                              onClick={() => handleDelete(platform)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">API Integration Notes</p>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• Credentials are securely stored and encrypted</li>
                  <li>• Test connection after saving to verify credentials</li>
                  <li>• Data sync runs automatically for active integrations</li>
                  <li>• Check platform documentation for obtaining API credentials</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
